import { z } from 'zod';
import { colorSchema, mmSchema } from './common.js';
// ---------------------------------------------------------------------------
// Zellen und Rollen (A.6, A.1 Grundsatz 4)
// ---------------------------------------------------------------------------
export const cellRoleSchema = z.enum(['given', 'solution', 'blank']);
export const cellSchema = z.object({
    /** Platz im Aufgabenformat, z. B. "r0c2", "roof", "ring3", "hand.minute". */
    slot: z.string().min(1),
    value: z.union([z.string(), z.number(), z.null()]),
    role: cellRoleSchema,
    style: z
        .object({
        fill: colorSchema.optional(),
        border: z.enum(['none', 'line', 'box']).optional(),
    })
        .optional(),
    distractors: z.array(z.union([z.string(), z.number()])).optional(),
});
export const TASK_KINDS = [
    'numberWall',
    'numberHouse',
    'numberTriangle',
    'magicSquare',
    'arrowChain',
    'operatorWheel',
    'calcWheel',
    'decomposition',
    'operations',
    'comparison',
    'arithmeticTable',
    'multiplicationGrid',
    'writtenMethod',
    'partialWrittenMethod',
    'numberLine',
    'numberChain',
    'tenFrame',
    'twentyFrame',
    'hundredFrame',
    'hundredChart',
    'clock',
    'geoboard',
    'wordProblem',
    // Phase 8.2: neue Formate (Erweiterung von Anhang A.6, abwärtskompatibel)
    'placeValueTable',
    'money',
    'baseTenBlocks',
    'trueFalse',
    'multipleChoice',
    'matching',
    'crossword',
    'syllablePuzzle',
    'phonicsTable',
];
export const taskKindSchema = z.enum(TASK_KINDS, { error: 'Unbekannte Aufgabenart' });
// ---------------------------------------------------------------------------
// Konfiguration (A.6)
// ---------------------------------------------------------------------------
export const numberRangeSchema = z
    .object({ min: z.number().int(), max: z.number().int() })
    .refine((r) => r.min <= r.max, { error: 'Zahlenraum: min muss kleiner oder gleich max sein' });
export const crossingSchema = z.enum(['with', 'without', 'any']);
/** Ziffernmuster, z. B. "x0" für Zehnerzahlen. */
export const digitPatternSchema = z.string().regex(/^[0-9x]+$/, {
    error: 'Ziffernmuster darf nur Ziffern und "x" enthalten',
});
export const operationSchema = z.enum(['add', 'sub', 'mul', 'div']);
const baseTaskConfigShape = {
    range: numberRangeSchema,
    /** Welche Slots `given` bzw. `solution` sind; je Aufgabenart definiert. */
    taskType: z.string().min(1),
    allowZero: z.boolean().optional(),
    patterns: z.record(z.string(), digitPatternSchema).optional(),
};
export const baseTaskConfigSchema = z.object(baseTaskConfigShape);
export const numberWallConfigSchema = z.object({
    ...baseTaskConfigShape,
    levels: z.number().int().min(2).max(6),
    operation: z.enum(['add', 'mul']),
});
export const operationsConfigSchema = z.object({
    ...baseTaskConfigShape,
    operations: z.array(operationSchema).min(1),
    strategy: z.enum(['none', 'analogy', 'inverse', 'swap', 'neighbour']).optional(),
    crossing: z
        .object({
        tens: crossingSchema.optional(),
        hundreds: crossingSchema.optional(),
        thousands: crossingSchema.optional(),
    })
        .optional(),
    timesTables: z.array(z.number().int().min(1).max(20)).optional(),
    remainder: z.boolean().optional(),
});
export const writtenMethodConfigSchema = z.object({
    ...baseTaskConfigShape,
    operation: operationSchema,
    operands: z.number().int().min(2).max(5),
    carry: z.boolean(),
    subtractionMethod: z.enum(['complement', 'borrow', 'bavarian']).optional(),
    remainder: z.boolean().optional(),
});
/** Zahlenhaus: Zielzahl im Dach, je Stockwerk eine Zerlegung (Phase 3.3). */
export const numberHouseConfigSchema = z.object({
    ...baseTaskConfigShape,
    floors: z.number().int().min(1).max(12),
});
export const clockConfigSchema = z.object({
    ...baseTaskConfigShape,
    given: z.enum(['hands', 'text']),
    restrict: z.enum(['full', 'half', 'quarter', '5min', 'any']),
    twoTimes: z.boolean(),
});
/**
 * Konfiguration einer Aufgabe. Konkrete Formate liefern je Phase ein eigenes
 * Schema; bis dahin erlaubt `genericTaskConfigSchema` zusätzliche Schlüssel,
 * damit Blätter aus späteren Phasen nicht an der Validierung scheitern.
 */
export const genericTaskConfigSchema = z.looseObject(baseTaskConfigShape);
export const taskConfigSchema = z.union([
    numberWallConfigSchema,
    numberHouseConfigSchema,
    operationsConfigSchema,
    writtenMethodConfigSchema,
    clockConfigSchema,
    genericTaskConfigSchema,
]);
/** Registry der Konfigurationsschemas je Aufgabenart (wird je Phase gefüllt). */
export const TASK_CONFIG_SCHEMAS = {
    numberWall: numberWallConfigSchema,
    numberHouse: numberHouseConfigSchema,
    operations: operationsConfigSchema,
    writtenMethod: writtenMethodConfigSchema,
    clock: clockConfigSchema,
};
// ---------------------------------------------------------------------------
// Darstellung
// ---------------------------------------------------------------------------
export const taskDisplaySchema = z.looseObject({
    cellWidth: mmSchema.optional(),
    cellHeight: mmSchema.optional(),
    gap: mmSchema.optional(),
    showOperators: z.boolean().optional(),
    placeholder: z.enum(['box', 'line', 'none']).optional(),
});
export const generatorRefSchema = z.object({
    /** z. B. "numberWall@1.0.0" */
    version: z.string().regex(/^[a-zA-Z][a-zA-Z0-9]*@\d+\.\d+\.\d+$/, {
        error: 'Generator-Version muss "name@1.0.0" sein',
    }),
    reseed: z.number().int().nonnegative(),
});
//# sourceMappingURL=task.js.map